import { createContext } from "react";
import generateUUID from "../utils/generate-uuid";
import { isEmptyValue } from "../utils/objects";
import { toTitleCase } from "../utils/strings";
import "./io-abstract.css";

// Will be initiated at higher level
export const EditContext = createContext(false);

export const Modes = Object.freeze({
  input: "input",
  output: "output"
});
export type ModesList = keyof typeof Modes;

export type IOPropsReadonly<T> = {
  label?: string;
  value?: T;
  mode?: ModesList;
  noLabel?: boolean;
  e2eExcluded?: boolean; // Wether the data should be excluded from e2e
  inputHelp?: React.ReactNode;
  appendix?: React.ReactNode;
  note?: boolean;
};

export type IOProps<T> = IOPropsReadonly<T> & {
  type?: string;
  inputType?: string;
  name?: string;
  required?: boolean;
  onChange?: (arg: T) => void;
  // To be used by sub-types
  htmlProps?: Record<string, string | number | Record<string, unknown>>;
};

function getLabel<T>(props: { label?: string; name?: string }): string {
  if (props.label) {
    return props.label;
  }
  if (props.name) {
    return toTitleCase(props.name);
  }
  return generateUUID();
}

export default function IOAbstract<T>(props: {
  // functionallity
  editContext: boolean;
  mode?: ModesList;
  required?: boolean;
  value?: T;
  renderOutput: React.ReactNode;
  renderInput?: {
    (uuid: string): React.ReactNode;
  };

  // rendering
  appendix?: React.ReactNode;
  currentError?: string | React.ReactNode;
  label?: string;
  name?: string;
  noLabel?: boolean;
  inputHelp?: React.ReactNode;

  // data and css
  e2eExcluded?: boolean; // Wether the data should be excluded from e2e
  inputType?: string;
  note?: boolean;
  type: string;
}): React.ReactNode {
  //
  // Some Checks
  //

  // Sanity check: extra label?
  if (
    props.name &&
    props.label &&
    props.label == toTitleCase(props.name ?? "")
  ) {
    throw new Error(
      `IOAbstract: do not specify label equivalent to name (${JSON.stringify(props)})`
    );
  }

  // Sanity check: no input while writable?
  if (props.name && !props.renderInput) {
    throw new Error(
      `IOAbstract: Need the Input for non-readonly inputs (${JSON.stringify(props)}}`
    );
  }

  //
  // Configs
  //

  const mode: ModesList =
    props.mode ?? (props.editContext ? Modes.input : Modes.output);
  const readOnly = !props.name;
  const empty =
    (mode == Modes.output && isEmptyValue(props.value)) ||
    (props.editContext && readOnly);
  const uuid = generateUUID();

  // We need the .io div for extracting the infos
  // And to be sure we are reliably displayed
  return (
    <div
      className={
        "io " +
        (props.note ? "io-note " : "") +
        (mode == Modes.input ? "io-input " : "io-output ") +
        `io-${props.type}`
      }
      data-role={getLabel(props)}
      data-input-type={props.inputType ?? props.type}
      data-empty={empty ? "true" : "false"}
      data-read-only={readOnly ? "true" : "false"}
    >
      {
        // Hide if not value and output mode
        !empty && (
          <>
            {props.noLabel || (
              <label htmlFor={uuid}>
                {getLabel(props)}
                {props.required ? "*" : ""}
              </label>
            )}
            <div
              className="content"
              data-e2e={props.e2eExcluded ? "excluded" : ""}
            >
              {mode == Modes.input ? (
                <>
                  {props.renderInput!(uuid)}
                  {props.currentError && (
                    <span className="io-error">{props.currentError}</span>
                  )}
                  {props.inputHelp}
                </>
              ) : (
                <>{props.renderOutput}</>
              )}
            </div>
            {props.appendix && <div className="appendix">{props.appendix}</div>}
          </>
        )
      }
    </div>
  );
}
